import { MarketStatus } from '@ps/isp-web-services-types';
import produce from 'immer';

import type { MarketPollingData } from '@swf/sports/types/market';
import updateMarket from '@swf/sports/utils/updateMarket';

import { defaultState } from './defaultState';
import type { NextRacesHandlerResponseBody, Template } from './types';

type State = typeof defaultState;

export const reducers = {
  clear: (): State => produce(defaultState, (draft) => draft),
  dataLoadSuccess: (state: State, payload: NextRacesHandlerResponseBody): State => {
    const { selectedTemplate, selectedTab } = state;
    const selectedMarket = selectedTemplate ? state.markets[selectedTemplate.marketId] : undefined;

    if (!selectedTemplate || payload.markets[selectedTemplate.marketId] || !selectedMarket) {
      return {
        ...state,
        ...payload,
        isLoading: false,
        hasPreloaded: true
      };
    }

    const { eventTypeId } = selectedTemplate;

    // At this point we know a user has moved between Sport pages eg. 7 to 4339
    if (!eventTypeId || !payload.eventTypes[eventTypeId]) {
      return {
        ...payload,
        isLoading: false,
        hasPreloaded: true,
        ukIeTemplates: [],
        selectedTab: ''
      };
    }

    const selectedMeeting = state.meetings[selectedMarket.meetingId];
    const selectedRace = state.races[selectedMarket.raceId];
    const isSelectedTemplateUkIe = state.ukIeTemplates.some(
      (template) => template.marketId === selectedTemplate.marketId
    );

    // At this point we know we have a selectedTemplate that has dropped off (finished)
    return {
      ...state,
      isLoading: false,
      hasPreloaded: true,
      templates: !isSelectedTemplateUkIe
        ? [
            {
              ...selectedTemplate,
              hasFinished: true
            },
            ...payload.templates
          ]
        : payload.templates,
      ukIeTemplates: isSelectedTemplateUkIe
        ? [
            {
              ...selectedTemplate,
              hasFinished: true
            },
            ...payload.ukIeTemplates
          ]
        : payload.ukIeTemplates,
      markets: {
        [selectedMarket.marketId]: {
          ...selectedMarket,
          marketStatus: MarketStatus.CLOSED
        },
        ...payload.markets
      },
      meetings: {
        [selectedMeeting.meetingId]: selectedMeeting,
        ...payload.meetings
      },
      races: {
        [selectedRace.raceId]: selectedRace,
        ...payload.races
      },
      selectedTab
    };
  },
  dataLoadFailed: (state: State, payload: number): State => ({
    ...state,
    isLoading: false,
    hasPreloaded: true,
    errorCode: payload
  }),
  nextRacesRequested: (state: State) => ({
    ...state,
    isLoading: true
  }),
  setSelected: (state: State, selectedTemplate: Template): State => ({
    ...state,
    selectedTemplate
  }),
  setSelectedTab: (state: State, selectedTab: string): State => ({
    ...state,
    selectedTab
  }),
  marketsUpdated: (state: State, { data: marketUpdates, closed }: MarketPollingData) =>
    produce(state, (draft) => {
      Object.values(draft.markets).forEach((market) => {
        if (!market) return;

        if (closed.includes(market.marketId)) {
          market.marketStatus = MarketStatus.CLOSED;
          return;
        }

        const marketUpdate = marketUpdates[market.marketId];

        if (marketUpdate) updateMarket(market, marketUpdate);
      });

      return draft;
    })
};
